import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AuthedImage from "@/components/AuthedImage";
import { toast } from "@/components/ui/sonner";
import { Trash2, Edit2, Globe, Lock, Save, X, FolderInput, Star, ImageDown, RotateCw, RotateCcw, ImagePlus } from "lucide-react";

export default function AlbumView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [allAlbums, setAllAlbums] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({ title: "", description: "", is_public: false });
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [photoEdit, setPhotoEdit] = useState({ caption: "", date_taken: "", location: "" });
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [showUploader, setShowUploader] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [dropActive, setDropActive] = useState(false);
  const fileInputRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/albums/${id}`);
      setAlbum(data);
      setEdit({ title: data.title, description: data.description || "", is_public: data.is_public });
      if (data.is_owner) {
        const a = await api.get("/albums");
        setAllAlbums(a.data.albums);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load album");
    }
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  const toggleVisibility = async () => {
    const next = !album.is_public;
    try {
      await api.patch(`/albums/${id}`, { is_public: next });
      toast.success(next ? "Album is now public 🌐" : "Album is now private 🔒");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update visibility");
    }
  };

  const moveToAlbum = async (photoId, newAlbumId) => {
    if (!newAlbumId) return;
    try {
      await api.patch(`/photos/${photoId}`, { album_id: newAlbumId, status: "public" });
      const dest = allAlbums.find((a) => a.id === newAlbumId);
      toast.success(`Moved to "${dest?.title || "album"}"`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't move photo");
    }
  };

  const setAsCover = async (photoId) => {
    try {
      await api.patch(`/albums/${id}`, { cover_photo_id: photoId });
      toast.success("Album cover updated");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't set cover");
    }
  };

  const setAsProfileHero = async (photoId) => {
    try {
      await api.patch("/profile", { profile_cover_photo_id: photoId });
      toast.success("Set as your profile hero ✨");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't set profile hero");
    }
  };

  const rotate = async (photoId, direction) => {
    try {
      await api.post(`/photos/${photoId}/rotate`, null, { params: { direction } });
      toast.success("Photo rotated");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rotation failed");
    }
  };

  const saveAlbum = async () => {
    await api.patch(`/albums/${id}`, edit);
    setEditing(false);
    reload();
  };

  const deleteAlbum = async () => {
    if (!confirm("Delete this album? Photos will not be deleted.")) return;
    await api.delete(`/albums/${id}`);
    navigate("/dashboard");
  };

  const savePhoto = async (photoId) => {
    await api.patch(`/photos/${photoId}`, photoEdit);
    setEditingPhoto(null);
    reload();
  };

  const removeFromAlbum = async (photoId) => {
    try {
      await api.patch(`/photos/${photoId}`, { album_id: null, status: "private" });
      toast.success("Removed from album (kept private)");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove photo");
    }
  };

  const deletePhoto = async (photoId) => {
    if (!confirm("Delete this photo?")) return;
    try {
      await api.delete(`/photos/${photoId}`);
      toast.success("Photo deleted");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete photo");
    }
  };

  const uploadPhotos = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setUploadBusy(true);
    setUploadProgress({ done: 0, total: files.length });
    let ok = 0;
    let quotaHit = false;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("album_id", id);
      try {
        await api.post("/photos/upload-self", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (e) {
        if (e.response?.status === 507) quotaHit = true;
      }
      setUploadProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setUploadBusy(false);
    setUploadProgress({ done: 0, total: 0 });
    setShowUploader(false);
    if (ok > 0) toast.success(`Added ${ok} photo${ok === 1 ? "" : "s"} to the album`);
    if (quotaHit) toast.error("Uploads are temporarily paused — we've reached our storage limit. Please try again later.");
    else if (ok < files.length) toast.error(`${files.length - ok} photo(s) failed to upload`);
    reload();
  };

  const onDrop = async (dropIdx) => {
    if (dragIndex === null || dragIndex === dropIdx) {
      setDragIndex(null); setOverIndex(null); return;
    }
    const photos = [...album.photos];
    const [moved] = photos.splice(dragIndex, 1);
    photos.splice(dropIdx, 0, moved);
    setAlbum({ ...album, photos });
    setDragIndex(null); setOverIndex(null);
    try {
      await api.post(`/albums/${id}/reorder`, { photo_ids: photos.map((p) => p.id) });
      toast.success("Order saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save order");
      reload();
    }
  };

  if (error) return <div className="max-w-7xl mx-auto px-6 py-16"><p data-testid="album-error" className="text-[#8C3A3A]">{error}</p></div>;
  if (!album) return <div className="max-w-7xl mx-auto px-6 py-16 text-[#8A8A8A]">Loading...</div>;

  return (
    <div className="paper-grain min-h-screen" data-testid="album-view-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
          <div>
            <Link to={album.is_owner ? "/dashboard" : `/u/${album.owner?.screen_name}`} className="text-sm text-[#5C5C5C] hover:text-[#8C3A3A]" data-testid="album-back-link">
              ← Back
            </Link>
            {editing ? (
              <input data-testid="album-title-input" className="mp-input mt-2 font-serif-display text-3xl" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}/>
            ) : (
              <h1 className="font-serif-display text-4xl mt-2" data-testid="album-title">{album.title}</h1>
            )}
            <p className="text-sm text-[#5C5C5C] mt-1">by <Link to={`/u/${album.owner?.screen_name}`} className="underline text-[#8C3A3A]">{album.owner?.screen_name}</Link></p>
          </div>
          {album.is_owner && (
            <div className="flex items-center gap-2">
              {editing ? (
                <>
                  <button onClick={saveAlbum} className="btn-primary text-sm" data-testid="album-save-btn"><Save className="w-4 h-4"/>Save</button>
                  <button onClick={() => setEditing(false)} className="btn-ghost text-sm" data-testid="album-cancel-btn"><X className="w-4 h-4"/></button>
                </>
              ) : (
                <>
                  <button onClick={() => setShowUploader((s) => !s)} className="btn-primary text-sm" data-testid="album-add-photo-btn"><ImagePlus className="w-4 h-4"/>Add photos</button>
                  <button onClick={() => setEditing(true)} className="btn-secondary text-sm" data-testid="album-edit-btn"><Edit2 className="w-4 h-4"/>Edit</button>
                  <button onClick={deleteAlbum} className="btn-ghost text-sm text-[#8C3A3A]" data-testid="album-delete-btn"><Trash2 className="w-4 h-4"/></button>
                </>
              )}
            </div>
          )}
        </div>

        {editing ? (
          <div className="paper-card p-4 mb-8 max-w-2xl">
            <textarea className="mp-input mb-3" placeholder="Album description..." value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })}/>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={edit.is_public} onChange={(e) => setEdit({ ...edit, is_public: e.target.checked })} data-testid="album-public-toggle"/>
              <span className="text-sm">Make album public (anyone can view)</span>
            </label>
          </div>
        ) : (
          <>
            {album.description && <p className="text-[#5C5C5C] mb-4 max-w-2xl">{album.description}</p>}
            <div className="flex items-center gap-3 mb-8">
              {album.is_owner ? (
                <button
                  onClick={toggleVisibility}
                  data-testid="album-visibility-toggle"
                  title={album.is_public ? "Public — click to make private" : "Private — click to make public"}
                  className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E5E0D8] hover:bg-[#F0EBE1] transition-colors"
                >
                  {album.is_public ? <><Globe className="w-3.5 h-3.5 text-[#3A5A40]"/> Public</> : <><Lock className="w-3.5 h-3.5 text-[#8C3A3A]"/> Private</>}
                  <span className="text-[#8A8A8A] normal-case tracking-normal">· tap to make {album.is_public ? "private" : "public"}</span>
                </button>
              ) : (
                <span className="text-xs uppercase tracking-[0.18em] flex items-center gap-2">
                  {album.is_public ? <><Globe className="w-3.5 h-3.5"/> Public</> : <><Lock className="w-3.5 h-3.5"/> Private</>}
                </span>
              )}
              <span className="text-xs text-[#8A8A8A]">· {album.photos.length} photos</span>
            </div>
          </>
        )}

        {showUploader && album.is_owner && (
          <div className="paper-card p-6 mb-8" data-testid="album-uploader">
            <div
              onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
              onDragLeave={() => setDropActive(false)}
              onDrop={(e) => { e.preventDefault(); setDropActive(false); if (!uploadBusy) uploadPhotos(e.dataTransfer.files); }}
              onClick={() => !uploadBusy && fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${dropActive ? "border-[#8C3A3A] bg-[#F0EBE1]" : "border-[#E5E0D8] hover:border-[#8C3A3A] hover:bg-[#F9F6F0]"}`}
              data-testid="album-dropzone"
            >
              <ImagePlus className="w-8 h-8 mx-auto text-[#8C3A3A] mb-2"/>
              <p className="text-[#5C5C5C]">Drag &amp; drop photos here, or click to choose</p>
              <p className="text-xs text-[#8A8A8A] mt-1">Select multiple at once. Each is resized to 1200px and published straight to this album.</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid="album-file-input"
                onChange={(e) => uploadPhotos(e.target.files)}
              />
            </div>
            {uploadBusy && (
              <p className="text-sm text-[#3A5A40] mt-3" data-testid="upload-progress">
                Uploading {uploadProgress.done} / {uploadProgress.total}…
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {album.is_owner && album.photos.length > 1 && (
            <p className="text-xs text-[#8A8A8A] col-span-full -mt-4" data-testid="reorder-hint">
              Tip: drag photos to reorder them within this album.
            </p>
          )}
          {album.photos.map((p, idx) => {
            const tilt = ["tilt-l", "tilt-r", "", "tilt-xr", "tilt-xl"][idx % 5];
            const isCover = album.cover_photo_id === p.id;
            const draggable = album.is_owner && editingPhoto !== p.id;
            return (
              <div
                key={p.id}
                className={`polaroid ${tilt} relative ${draggable ? "cursor-move" : ""} ${overIndex === idx ? "ring-2 ring-[#8C3A3A] ring-offset-2" : ""} ${dragIndex === idx ? "opacity-40" : ""}`}
                data-testid={`photo-card-${p.id}`}
                draggable={draggable}
                onDragStart={() => draggable && setDragIndex(idx)}
                onDragOver={(e) => { if (album.is_owner) { e.preventDefault(); setOverIndex(idx); } }}
                onDragLeave={() => setOverIndex((v) => (v === idx ? null : v))}
                onDrop={(e) => { e.preventDefault(); onDrop(idx); }}
                onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
              >
                {isCover && (
                  <span
                    data-testid={`cover-badge-${p.id}`}
                    className="absolute -top-2 -right-2 z-10 bg-[#3A5A40] text-[#F9F6F0] text-[10px] uppercase tracking-[0.18em] px-2 py-1 flex items-center gap-1"
                  >
                    <Star className="w-3 h-3 fill-current"/> Cover
                  </span>
                )}
                <div className="aspect-[4/3] overflow-hidden bg-[#F0EBE1]">
                  <AuthedImage key={`${p.id}-${p.rotated_at || ''}`} photoId={p.id} className="w-full h-full object-cover"/>
                </div>
                {editingPhoto === p.id ? (
                  <div className="mt-2 space-y-2">
                    <input className="mp-input text-xs" placeholder="Caption" value={photoEdit.caption} onChange={(e) => setPhotoEdit({ ...photoEdit, caption: e.target.value })}/>
                    <input className="mp-input text-xs" placeholder="Date (YYYY-MM-DD)" value={photoEdit.date_taken} onChange={(e) => setPhotoEdit({ ...photoEdit, date_taken: e.target.value })}/>
                    <input className="mp-input text-xs" placeholder="Location" value={photoEdit.location} onChange={(e) => setPhotoEdit({ ...photoEdit, location: e.target.value })}/>
                    <div className="flex gap-2">
                      <button onClick={() => savePhoto(p.id)} className="btn-primary text-xs flex-1" data-testid={`photo-save-${p.id}`}>Save</button>
                      <button onClick={() => setEditingPhoto(null)} className="btn-ghost text-xs">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="font-script text-lg mt-2 text-center">{p.caption || (p.location ? p.location : "")}</p>
                    <p className="text-xs text-center text-[#8A8A8A]">{p.date_taken || ""}</p>
                    {p.uploader_email && album.is_owner && (
                      <p className="text-[10px] text-center text-[#8A8A8A] mt-1" data-testid={`photo-submitter-${p.id}`}>
                        Submitted by: {p.uploader_email}
                      </p>
                    )}
                    {album.is_owner && (
                      <div className="mt-2 space-y-1">
                        <select
                          data-testid={`photo-move-${p.id}`}
                          defaultValue=""
                          onChange={(e) => moveToAlbum(p.id, e.target.value)}
                          className="mp-input text-xs w-full"
                          title="Move to another album"
                        >
                          <option value="">Move to album…</option>
                          {allAlbums
                            .filter((a) => a.id !== album.id)
                            .map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.title}
                              </option>
                            ))}
                        </select>
                        <div className="flex gap-1 justify-center">
                          <button
                            onClick={() => setAsCover(p.id)}
                            className={`text-xs btn-ghost ${isCover ? "text-[#3A5A40]" : ""}`}
                            title={isCover ? "Current cover" : "Set as album cover"}
                            disabled={isCover}
                            data-testid={`photo-set-cover-${p.id}`}
                          >
                            <Star className={`w-3 h-3 ${isCover ? "fill-current" : ""}`}/>
                          </button>
                          {p.status === "public" && (
                            <button
                              onClick={() => setAsProfileHero(p.id)}
                              className="text-xs btn-ghost"
                              title="Set as profile hero"
                              data-testid={`photo-set-hero-${p.id}`}
                            >
                              <ImageDown className="w-3 h-3"/>
                            </button>
                          )}
                          <button
                            onClick={() => rotate(p.id, "ccw")}
                            className="text-xs btn-ghost"
                            title="Rotate 90° left"
                            data-testid={`photo-rotate-ccw-${p.id}`}
                          >
                            <RotateCcw className="w-3 h-3"/>
                          </button>
                          <button
                            onClick={() => rotate(p.id, "cw")}
                            className="text-xs btn-ghost"
                            title="Rotate 90° right"
                            data-testid={`photo-rotate-cw-${p.id}`}
                          >
                            <RotateCw className="w-3 h-3"/>
                          </button>
                          <button onClick={() => { setEditingPhoto(p.id); setPhotoEdit({ caption: p.caption || "", date_taken: p.date_taken || "", location: p.location || "" }); }} className="text-xs btn-ghost" data-testid={`photo-edit-${p.id}`}><Edit2 className="w-3 h-3"/></button>
                          <button onClick={() => removeFromAlbum(p.id)} className="text-xs btn-ghost" title="Remove from album (keeps photo private)" data-testid={`photo-remove-${p.id}`}>↶</button>
                          <button onClick={() => deletePhoto(p.id)} className="text-xs btn-ghost text-[#8C3A3A]" data-testid={`photo-delete-${p.id}`}><Trash2 className="w-3 h-3"/></button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
          {album.photos.length === 0 && (
            <p className="text-[#8A8A8A] italic col-span-full text-center py-12">No photos in this album yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
